import React from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import type { MetricAutoAddRuleSource } from "@/metrics/runtime-adapters"
import { Button, Switch, useHoverOpen } from "@coval/ui"
import { BarChart3, CheckSquare, Dna, Sparkles } from "lucide-react"

import {
  type MetricValueFilter as MetricFilter,
  type MetricValueFilterOperator as MetricFilterOperator,
} from "@/lib/metricValueFilters"
import { ActiveFilterBar } from "@/components/filters/ActiveFilterBar"
import { CollectionFilter } from "@/components/filters/CollectionFilter"
import { EditableFilterChip } from "@/components/filters/EditableFilterChip"
import { FilterPopover } from "@/components/filters/FilterPopover"
import {
  SearchableCheckboxList,
  type SearchableItem,
} from "@/components/filters/SearchableCheckboxList"
import type { CollectionFilterControls } from "@/hooks/useCollectionFilters"
import type { RunRowLike } from "@/types/RunRow"
import {
  formatMetricDisplayName,
  resolveMetricDisplayName,
} from "@/utils/formatting/format-metric-display-results"

import { useRunFilterContext } from "../contexts/RunFilterContext"
import {
  MetricsSection,
  MutationsFilterSection,
  StatusFilterSection,
} from "./RunFilterSections"

export {
  getDefaultFilterValue,
  getMetricFilterInfo,
  resolveMetricFilterInfo,
} from "./RunFilterSections"

interface RunFilterDropdownProps {
  onClearAllFilters?: () => void
  propertyFilters?: CollectionFilterControls
  /** When provided, the dropdown open state is controlled by the parent. */
  section?: string | null
  onSectionChange?: (section: string | null) => void
  isOpen?: boolean
  /** When provided with isOpen, called when the dropdown open state changes. */
  onOpenChange?: (open: boolean) => void
  /** Mutation id -> display name (from run rows). */
  mutationNameById: Map<string, string>
  /** Mutation filter: selected mutation IDs (IDs under the hood). */
  mutationFilterIds: string[]
  onMutationFilterChange: (ids: string[]) => void
  /** Status filter: selected statuses (e.g. COMPLETED, FAILED). */
  statusFilterStatuses: string[]
  onStatusFilterChange: (statuses: string[]) => void
  isOutliersEnabled?: boolean
  onOutliersEnabledChange?: (enabled: boolean) => void
  outlierMetricIds?: string[] | null
  outlierMetricOptions?: SearchableItem[]
  onOutlierMetricIdsChange?: (ids: string[] | null) => void
  /** Metric filter state and handlers (for Metrics section, same as MetricFilterDropdown). */
  metricRecordsById: Record<string, MetricIdentity>
  /** Richer per-metric source carrying categorical options + metadata for value pre-fill. */
  metricSourcesById: Record<string, MetricAutoAddRuleSource>
  runRows: RunRowLike[]
  metricFilters: MetricFilter[]
  onAddMetricFilter: (
    metricName: string,
    operator: MetricFilterOperator,
    value: number | string | null,
    rangeMax?: number,
  ) => void
  onRemoveMetricFilter: (index: number) => void
  onClearAllMetricFilters: () => void
}

export const RunFilterDropdown: React.FC<RunFilterDropdownProps> = ({
  onClearAllFilters,
  propertyFilters,
  section,
  onSectionChange,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  mutationNameById,
  mutationFilterIds,
  onMutationFilterChange,
  statusFilterStatuses,
  onStatusFilterChange,
  isOutliersEnabled = false,
  onOutliersEnabledChange,
  outlierMetricIds = null,
  outlierMetricOptions = [],
  onOutlierMetricIdsChange,
  metricRecordsById,
  metricSourcesById,
  runRows,
  metricFilters,
  onAddMetricFilter,
  onRemoveMetricFilter,
  onClearAllMetricFilters,
}) => {
  const { totalActiveFilters: contextFilterCount } = useRunFilterContext()
  return (
    <CollectionFilter
      controls={
        propertyFilters ?? {
          properties: [],
          values: {},
          onChange: () => {},
          reset: () => {},
          activeCount: 0,
        }
      }
      label="Filter results"
      onReset={() => {
        onClearAllFilters?.()
        onStatusFilterChange([])
        onMutationFilterChange([])
        onClearAllMetricFilters()
        onOutliersEnabledChange?.(false)
        onOutlierMetricIdsChange?.(null)
      }}
      activeCount={
        contextFilterCount +
        mutationFilterIds.length +
        metricFilters.length +
        statusFilterStatuses.length +
        Number(isOutliersEnabled)
      }
      open={controlledIsOpen}
      onOpenChange={controlledOnOpenChange}
      section={section}
      onSectionChange={onSectionChange}
      items={[
        {
          id: "status",
          label: "Status",
          icon: <CheckSquare />,
          activeCount: statusFilterStatuses.length,
          content: (
            <StatusFilterSection
              isOpen
              onOpenChange={() => {}}
              selectedStatuses={statusFilterStatuses}
              onStatusFilterChange={onStatusFilterChange}
            />
          ),
        },
        {
          id: "mutations",
          label: "Mutation",
          icon: <Dna />,
          activeCount: mutationFilterIds.length,
          content: (
            <MutationsFilterSection
              isOpen
              onOpenChange={() => {}}
              selectedMutationIds={mutationFilterIds}
              onMutationFilterChange={onMutationFilterChange}
              mutationNameById={mutationNameById}
            />
          ),
        },
        {
          id: "metrics",
          label: "Metric",
          keywords: "score value",
          icon: <BarChart3 />,
          activeCount: metricFilters.length,
          content: (
            <MetricsSection
              isOpen
              onOpenChange={() => {}}
              metricRecordsById={metricRecordsById}
              metricSourcesById={metricSourcesById}
              runRows={runRows}
              filters={metricFilters}
              onAddFilter={onAddMetricFilter}
              onRemoveFilter={onRemoveMetricFilter}
              onClearAllFilters={onClearAllMetricFilters}
            />
          ),
        },
        ...(onOutliersEnabledChange
          ? [
              {
                id: "outliers",
                label: "Outliers",
                icon: <Sparkles />,
                activeCount: Number(isOutliersEnabled),
                content: (
                  <>
                    <div className="flex items-center justify-between gap-3 p-4">
                      <label htmlFor="run-outliers-toggle" className="text-sm">
                        Highlight outliers
                      </label>
                      <Switch
                        id="run-outliers-toggle"
                        checked={isOutliersEnabled}
                        onCheckedChange={onOutliersEnabledChange}
                      />
                    </div>
                    {isOutliersEnabled && onOutlierMetricIdsChange && (
                      <OutlierMetricsSection
                        isOpen
                        onOpenChange={() => {}}
                        metricOptions={outlierMetricOptions}
                        selectedMetricIds={outlierMetricIds}
                        onSelectionChange={onOutlierMetricIdsChange}
                      />
                    )}
                  </>
                ),
              },
            ]
          : []),
      ]}
    >
      <RunResultsFilterChips
        mutationFilterIds={mutationFilterIds}
        mutationNameById={mutationNameById}
        statusFilterStatuses={statusFilterStatuses}
        metricFilters={metricFilters}
        metricRecordsById={metricRecordsById}
        onRemoveMetricFilter={onRemoveMetricFilter}
        onClearStatuses={() => onStatusFilterChange([])}
        onClearMutations={() => onMutationFilterChange([])}
        onEditFilter={(next) => {
          onSectionChange?.(next)
          controlledOnOpenChange?.(true)
        }}
      />
      {isOutliersEnabled && (
        <EditableFilterChip
          property="Outliers"
          value="Highlighted"
          onRemove={() => {
            onOutliersEnabledChange?.(false)
            onOutlierMetricIdsChange?.(null)
          }}
        />
      )}
    </CollectionFilter>
  )
}

export function OutlierMetricsSection({
  isOpen,
  metricOptions,
  onOpenChange,
  onSelectionChange,
  selectedMetricIds,
}: {
  isOpen: boolean
  metricOptions: SearchableItem[]
  onOpenChange: (open: boolean) => void
  onSelectionChange: (ids: string[] | null) => void
  selectedMetricIds: string[] | null
}) {
  const hoverProps = useHoverOpen({ open: isOpen, onOpenChange })
  const selectedIds =
    selectedMetricIds ?? metricOptions.map((metric) => metric.id)
  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={() => onOpenChange(!isOpen)}
      trigger={
        <button
          type="button"
          {...hoverProps}
          className="flex w-full items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-surface-1"
        >
          <span>Outlier metrics</span>
          <span className="text-coval-text-muted">
            {selectedIds.length}/{metricOptions.length}
          </span>
        </button>
      }
    >
      <div className="flex items-center justify-between border-b border-coval-border px-3 py-2">
        <span className="text-sm font-medium">Outlier metrics</span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            aria-label="Select all outlier metrics"
            onClick={() => onSelectionChange(null)}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="ghost"
            aria-label="Clear outlier metrics"
            onClick={() => onSelectionChange([])}
          >
            Clear
          </Button>
        </div>
      </div>
      <SearchableCheckboxList
        items={metricOptions}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        isLoading={false}
        isOpen={isOpen}
        placeholder="Search outlier metrics..."
        emptyMessage="No outlier metrics found"
        noItemsMessage="No metric samples available"
      />
    </FilterPopover>
  )
}

/** Run-result chips share one clear action with property and outlier filters. */
export const RunResultsFilterChips: React.FC<{
  hasAdditionalFilters?: boolean
  mutationFilterIds: string[]
  mutationNameById: Map<string, string>
  statusFilterStatuses: string[]
  metricFilters: MetricFilter[]
  metricRecordsById: Record<string, MetricIdentity>
  onRemoveMetricFilter: (index: number) => void
  onClearAllFilters?: () => void
  onClearStatuses: () => void
  onClearMutations: () => void
  onEditFilter?: (section: string) => void
}> = ({
  hasAdditionalFilters = false,
  mutationFilterIds,
  mutationNameById,
  statusFilterStatuses,
  metricFilters,
  metricRecordsById,
  onRemoveMetricFilter,
  onClearAllFilters,
  onClearStatuses,
  onClearMutations,
  onEditFilter,
}) => {
  const hasAnyFilters =
    hasAdditionalFilters ||
    mutationFilterIds.length > 0 ||
    metricFilters.length > 0 ||
    statusFilterStatuses.length > 0
  if (!hasAnyFilters) {
    return null
  }

  const chips = (
    <div
      className="flex min-w-0 flex-wrap items-center gap-1.5"
      style={{ scrollbarWidth: "none" }}
    >
      {mutationFilterIds.length > 0 && (
        <EditableFilterChip
          property="Mutation"
          value={mutationFilterIds
            .map((id) => mutationNameById.get(id) ?? id)
            .join(", ")}
          onEdit={onEditFilter ? () => onEditFilter("mutations") : undefined}
          onRemove={onClearMutations}
        />
      )}
      {statusFilterStatuses.length > 0 && (
        <EditableFilterChip
          property="Status"
          value={statusFilterStatuses
            .map((status) => status.toLowerCase())
            .join(", ")}
          onEdit={onEditFilter ? () => onEditFilter("status") : undefined}
          onRemove={onClearStatuses}
        />
      )}
      {metricFilters.map((filter, index) => {
        const metricLabel = resolveMetricDisplayName(
          metricRecordsById[filter.metricName]?.metric_display_name,
          filter.metricName,
        )
        const isExistence = filter.operator === "exists"
        const label = isExistence
          ? metricLabel
          : `${metricLabel} ${
              filter.operator === "range"
                ? `${filter.value} – ${filter.rangeMax}`
                : filter.valueType === "string" &&
                    typeof filter.value === "string"
                  ? `${filter.operator} ${formatMetricDisplayName(filter.value)}`
                  : `${filter.operator} ${String(filter.value).toLowerCase()}`
            }`
        return (
          <EditableFilterChip
            key={`metric-${index}`}
            property={isExistence ? "Has Metric" : "Metric"}
            operator={null}
            value={label}
            onEdit={onEditFilter ? () => onEditFilter("metrics") : undefined}
            onRemove={() => onRemoveMetricFilter(index)}
          />
        )
      })}
    </div>
  )
  return onClearAllFilters ? (
    <ActiveFilterBar onClear={onClearAllFilters}>{chips}</ActiveFilterBar>
  ) : (
    chips
  )
}
